import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addPassenger, updatePassenger, deletePassenger, getPassengers } from '../redux/slices/userSlice';

const EmptyPassengerState = ({ onAddNew }) => {
    return (
        <div className="bg-custom-white p-6 rounded-lg border shadow-md flex flex-col items-center">
            <p className="text-gray-400 mb-4">No Passengers added</p>
            <div className="text-gray-300 text-4xl mb-4">🍃</div>
            <button
                onClick={onAddNew}
                className="bg-custom-yellow text-custom-white px-4 py-2 rounded-md w-full hover:bg-yellow-600"
            >
                + ADD NEW
            </button>
        </div>
    );
};

const PassengerForm = ({ onSave, onCancel, passenger = {} }) => {
    const [formData, setFormData] = useState({
        firstName: passenger?.firstName || '',
        lastName: passenger?.lastName || '',
        email: passenger?.email || '',
        phoneCountry: passenger?.phoneCountry || '+91',
        phoneNumber: passenger?.phoneNumber || '',
        gender: passenger?.gender || 'Male',
        dob: passenger?.dob || '',
        trips: passenger?.trips || 0,
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSave = () => {
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-custom-white p-6 rounded-lg shadow-md w-1/2">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-custom-blue">Edit Passenger Details</h2>
                    <button onClick={onCancel} className="text-custom-yellow">
                        <span className="text-2xl">⨂</span>
                    </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm text-gray-400">First Name*</label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-md"
                            placeholder="First Name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400">Last Name</label>
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-md"
                            placeholder="Last Name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-md"
                            placeholder="xyz@gmail.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400">Phone</label>
                        <div className="flex">
                            <select
                                name="phoneCountry"
                                value={formData.phoneCountry}
                                onChange={handleChange}
                                className="p-2 border rounded-md mr-2"
                            >
                                <option value="+91">+91</option>
                            </select>
                            <input
                                type="text"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                className="w-full p-2 border rounded-md"
                                placeholder="8765432190"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400">Gender</label>
                        <select
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-md"
                        >
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400">Date Of Birth</label>
                        <input
                            type="date"
                            name="dob"
                            value={formData.dob}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400">Trips</label>
                        <input
                            type="number"
                            name="trips"
                            value={formData.trips}
                            onChange={handleChange}
                            className="w-full p-2 border rounded-md"
                            placeholder="0"
                        />
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    className="bg-custom-yellow text-custom-white px-4 py-2 rounded-md w-full mt-4 hover:bg-yellow-600"
                >
                    SAVE DETAILS
                </button>
            </div>
        </div>
    );
};

const PassengerList = ({ passengers, onEdit, onDelete }) => {
    return (
        <div className="bg-custom-white p-6 rounded-lg border shadow-md">
            <h2 className="text-xl font-semibold text-custom-blue border-b pb-2">My Passengers</h2>
            {passengers.length === 0 ? (
                <p className="text-gray-400 py-4">No passengers available</p>
            ) : (
                passengers.map((passenger, index) => (
                    <div key={passenger.email || index} className="flex items-center border-b py-4 justify-between mb-4">
                        <div className="flex items-center">
                            <div className="w-10 h-10 bg-custom-yellow text-custom-white rounded-full flex items-center justify-center mr-3">
                                {passenger.firstName?.[0] || 'N'}{passenger.lastName?.[0] || 'A'}
                            </div>
                            <div>
                                <p className="text-lg text-custom-blue">
                                    {passenger.firstName} {passenger.lastName} - {passenger.gender}{' '}
                                    {passenger.trips && `(${passenger.trips})`}
                                </p>
                                <p className="text-sm text-gray-400">{passenger.email}</p>
                            </div>
                        </div>
                        <div>
                            <button onClick={() => onEdit(passenger)} className="text-custom-blue mr-4">
                                Edit
                            </button>
                            <button onClick={() => onDelete(passenger.email)} className="text-red-500">
                                Delete
                            </button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

const MyPassengers = () => {
    const dispatch = useDispatch();
    const passengers = useSelector((state) => state.user.passengerList || []);
    const loading = useSelector((state) => state.user.loading);
    const error = useSelector((state) => state.user.error);
    const [showForm, setShowForm] = useState(false);
    const [editingPassenger, setEditingPassenger] = useState(null);

    useEffect(() => {
        dispatch(getPassengers());
    }, [dispatch]);

    const handleAddNew = () => {
        setShowForm(true);
        setEditingPassenger(null);
    };

    const handleEdit = (passenger) => {
        setEditingPassenger(passenger);
        setShowForm(true);
    };

    const handleDelete = async (email) => {
        try {
            await dispatch(deletePassenger(email)).unwrap();
        } catch (error) {
            console.error('Failed to delete passenger:', error);
        }
    };

    const handleSave = async (formData) => {
        try {
            if (editingPassenger) {
                await dispatch(updatePassenger({ email: editingPassenger.email, data: formData })).unwrap();
            } else {
                await dispatch(addPassenger(formData)).unwrap();
            }
            dispatch(getPassengers());
            setShowForm(false);
            setEditingPassenger(null);
        } catch (error) {
            console.error('Failed to save passenger:', error);
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingPassenger(null);
    };

    return (
        <div className="p-6">
            {loading && <p className="text-gray-500">Loading passengers...</p>}
            {error && <p className="text-red-500">Error: {error}</p>}
            <div className="flex justify-end items-center mb-4">
                <button
                    onClick={handleAddNew}
                    className="text-custom-yellow font-semibold"
                >
                    + ADD NEW
                </button>
            </div>
            <PassengerList passengers={passengers} onEdit={handleEdit} onDelete={handleDelete} />
            {showForm && (
                <PassengerForm
                    onSave={handleSave}
                    onCancel={handleCancel}
                    passenger={editingPassenger}
                />
            )}
        </div>
    );
};

export default MyPassengers;